import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { TournamentRound, RoundStatus } from '@/types/tournament-round.model';

interface RoundsTimelineProps {
  rounds: TournamentRound[];
  selectedRoundId: string | null;
  onSelectRound: (roundId: string) => void;
}

/** Badge label and color by round status. */
function getRoundStatusBadge(status: RoundStatus): { label: string; className: string } {
  switch (status) {
    case 'CREATED':
      return { label: 'Por abrir', className: 'bg-gray-100 text-gray-700 border-gray-200' };
    case 'AVAILABILITY_OPEN':
      return { label: 'Cargando disponibilidad', className: 'bg-yellow-100 text-yellow-800 border-yellow-200' };
    case 'PROPOSED':
      return { label: 'Propuesta lista', className: 'bg-blue-100 text-blue-800 border-blue-200' };
    case 'PUBLISHED':
      return { label: 'Publicada', className: 'bg-green-100 text-green-700 border-green-200' };
    case 'COMPLETED':
      return { label: 'Cerrada', className: 'bg-slate-100 text-slate-600 border-slate-200' };
    default:
      return { label: status, className: 'bg-gray-100 text-gray-700' };
  }
}

/** Formats an ISO date into a short readable label. */
function formatWeekDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
}

/** List of rounds with status, week and submission progress. */
export function RoundsTimeline({ rounds, selectedRoundId, onSelectRound }: RoundsTimelineProps) {
  if (rounds.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-muted-foreground">
        No hay jornadas generadas todavía.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {rounds.map((round) => {
        const { label, className } = getRoundStatusBadge(round.status);
        const isSelected = round.id === selectedRoundId;

        // Availability submission progress
        const progress = round.availabilityProgress ?? [];
        const totalTeams = progress.length;
        const loadedTeams = progress.filter((p) => p.loaded).length;
        const progressText = totalTeams > 0 ? `${loadedTeams}/${totalTeams} cargaron` : null;

        const weekLabel = round.weekStartDate
          ? round.weekEndDate
            ? `${formatWeekDate(round.weekStartDate)} – ${formatWeekDate(round.weekEndDate)}`
            : `Sem. ${formatWeekDate(round.weekStartDate)}`
          : null;

        return (
          <button
            key={round.id}
            type="button"
            onClick={() => onSelectRound(round.id)}
            className={cn(
              'flex w-full items-center justify-between gap-3 rounded-lg border p-3 text-left transition-all',
              isSelected
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50',
            )}
          >
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className={cn('font-semibold', isSelected ? 'text-blue-700' : 'text-gray-900')}>
                  Fecha {round.roundNumber}
                </span>
                {weekLabel && (
                  <span className="text-xs text-muted-foreground">{weekLabel}</span>
                )}
              </div>
              {progressText && (
                <span className="text-xs text-muted-foreground">{progressText}</span>
              )}
            </div>

            <Badge variant="outline" className={cn('shrink-0 text-xs', className)}>
              {label}
            </Badge>
          </button>
        );
      })}
    </div>
  );
}
